import { useEffect, useRef, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import type { Message } from '../../../types/chat'
import type { MemberControls } from '../../../types/memberControls'
import { useChatControls } from '../../../hooks/useChatControls'
import { useWorkspaceDraft } from '../../../hooks/useWorkspaceDraft'
import { useNls } from '../../../nls/nlsProvider'
import { IconButton } from '../../Common/IconButton'
import { Textarea, ControlNotice } from '../../Common'
import { replyWindow } from '../../../utils/whatsappChat'

type Props = {
  channelName: string
  replyTo: Message | null
  onClearReplyTo: () => void
  disabled?: boolean
  onSend: (text: string, options: { replyToId?: string }) => void | Promise<void>
  onAttach?: () => void
  onOpenAi?: () => void
  prefill?: string | null
  onPrefillConsumed?: () => void
  workspaceId?: string
  channel?: Record<string, unknown> | null
  effectiveControls?: MemberControls | null
  whatsappRoute?: 'TIMELINING' | 'OWN' | string
}

const summarize = (text: string, cap = 90): string => {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim()
  return trimmed.length > cap ? `${trimmed.slice(0, cap - 1)}…` : trimmed
}

export const MemberChatComposer = ({
  channelName,
  replyTo,
  onClearReplyTo,
  disabled = false,
  onSend,
  onAttach,
  onOpenAi,
  prefill,
  onPrefillConsumed,
  workspaceId,
  channel,
  effectiveControls,
  whatsappRoute,
}: Props) => {
  const { t } = useNls()
  const channelId = typeof channel?.id === 'string' ? channel.id : ''
  const draftKey = channelId ? `draft.chat.${channelId}` : ''
  const [draftValue, setDraftValue, clearDraft] = useWorkspaceDraft(draftKey)
  const [sending, setSending] = useState(false)
  const [prevPrefill, setPrevPrefill] = useState<string | undefined>()
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const value = draftValue
  const setValue = setDraftValue

  const { noticeMessage, canSend: canSendByControl } = useChatControls({
    workspaceId,
    channel,
    effectiveControls,
  })

  const isComposerDisabled = disabled || !canSendByControl

  if (prefill && prefill !== prevPrefill) {
    setPrevPrefill(prefill)
    setValue(prefill)
  }

  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [value])

  useEffect(() => {
    if (replyTo) inputRef.current?.focus()
  }, [replyTo])

  useEffect(() => {
    if (!prefill) return
    onPrefillConsumed?.()
    window.requestAnimationFrame(() => inputRef.current?.focus())
  }, [prefill, onPrefillConsumed])

  const canSend = value.trim().length > 0 && !isComposerDisabled && !sending

  const submit = async () => {
    if (!canSend) return
    const text = value.trim()
    const replyToId = replyTo?.id
    setSending(true)
    clearDraft()
    onClearReplyTo()
    try {
      await onSend(text, replyToId ? { replyToId } : {})
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  return (
    <div className="memberChatComposerWrap">
      {noticeMessage ? (
        <ControlNotice
          tone="warning"
          message={noticeMessage}
          testId="chat-control-notice"
          className="memberChatControlNotice"
        />
      ) : null}
      {channel?.identityType === 'WHATSAPP' && channel.whatsappPaused ? (
        <ControlNotice
          tone="warning"
          message={t('NLSChatNoticePaused')}
          testId="whatsapp-paused-notice"
          className="memberChatControlNotice"
        />
      ) : null}
      {channel?.identityType === 'WHATSAPP' ? (
        <div className="memberChatWhatsappHints" data-testid="whatsapp-composer-hints">
          {(() => {
            const isTimeliningRoute = whatsappRoute !== 'OWN'
            const windowState = replyWindow(channel.whatsappLastInboundAt)
            return (
              <>
                {!windowState.open && (
                  <div className="whatsappClosedWindowHint" data-testid="whatsapp-closed-window-hint">
                    {t('NLSChatHintClosedWindow')}
                  </div>
                )}
                {isTimeliningRoute && (
                  <div className="whatsappCostHint" data-testid="whatsapp-cost-hint">
                    {t('NLSChatHintCost')}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      ) : null}
      {replyTo ? (
        <div className="memberChatReplyChip" role="note" aria-label={t('NLSChatAriaReplyingTo', { author: replyTo.authorName })}>
          <span className="memberChatReplyChip__bar" aria-hidden="true" />
          <div className="memberChatReplyChip__body">
            <span className="memberChatReplyChip__label">
              {t('NLSChatLabelReplyingTo', { author: replyTo.authorName })}
            </span>
            <span className="memberChatReplyChip__preview">
              {replyTo.isDeleted ? t('NLSChatMessageDeleted') : summarize(replyTo.textContent) || t('NLSChatAttachmentGeneric')}
            </span>
          </div>
          <IconButton
            size="sm"
            variant="tertiary"
            onClick={onClearReplyTo}
            label={t('NLSChatActionCancelReply')}
            icon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
          />
        </div>
      ) : null}
      <div className="memberChatComposer">
        {onAttach ? (
          <IconButton
            size="md"
            variant="tertiary"
            onClick={onAttach}
            label={t('NLSChatActionAttach')}
            disabled={isComposerDisabled}
            icon={<AddRoundedIcon fontSize="small" />}
          />
        ) : null}
        {onOpenAi ? (
          <IconButton
            size="md"
            variant="tertiary"
            onClick={onOpenAi}
            label={t('NLSChatAriaOpenAiMagic')}
            disabled={isComposerDisabled}
            icon={<AutoAwesomeRoundedIcon fontSize="small" />}
          />
        ) : null}
        <Textarea
          ref={inputRef}
          className="memberChatComposer__input"
          rows={1}
          value={value}
          onChange={setValue}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault()
              void submit()
            }
          }}
          placeholder={t('NLSChatComposerPlaceholder', { channel: channelName })}
          aria-label={t('NLSChatComposerPlaceholder', { channel: channelName })}
          disabled={isComposerDisabled}
        />
        <IconButton
          size="md"
          variant="primary"
          onClick={() => void submit()}
          disabled={!canSend}
          label={t('NLSChatActionSendMessage')}
          icon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
        />
      </div>
    </div>
  )
}
